import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Loader2 } from 'lucide-react';
import { PageWrapper } from '../components/PageWrapper';
import {
  getSettings,
  getEnabledGoals,
  isAICompanionEnabled,
  getAIWelcomeCache,
  saveAIWelcomeCache,
} from '../utils/storage';
import { getDailyPrompt } from '../data/prompts';
import { generateWelcomeMessage } from '../utils/aiCompanion';

const ACROSTIC_FONT_SIZE = 12.6;

const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

const easyLine = (easyText) => {
  if (!easyText) return null;
  const lower = easyText.toLowerCase().trim();
  const suffix = lower.endsWith('breathe') ? '.' : ' and breathe.';
  return `If you can't — just ${easyText}${suffix}`;
};

const FrameworkBlock = ({ children, colorVar, delay, compact = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 12, scale: 0.8 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ delay, duration: 0.3, ease: 'easeOut' }}
    style={{
      minHeight: 24,
      height: 24,
      borderRadius: 3,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: `var(${colorVar})`,
      padding: compact ? '0 8px' : '0 10px',
      flexShrink: 0,
    }}
  >
    {children}
  </motion.div>
);

const AcrosticRow = ({ word, delay }) => (
  <motion.div
    initial={{ opacity: 0, x: -12 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay, duration: 0.3, ease: 'easeOut' }}
    className="flex items-center"
  >
    <FrameworkBlock colorVar="--theme-primary" delay={delay}>
      <span className="acrostic-logo-text" style={{ fontSize: ACROSTIC_FONT_SIZE, fontFamily: 'var(--font-heading)', letterSpacing: 0.2 }}>
        <span className="acrostic-logo-text" style={{ fontWeight: 700 }}>{word.charAt(0).toUpperCase()}</span>
        <span className="acrostic-logo-text">{word.slice(1).toLowerCase()}</span>
      </span>
    </FrameworkBlock>
  </motion.div>
);

const LETTER_COLORS = {
  ME: '--theme-primary',
};

const acrosticRows = ['Rebuild', 'Everyday', 'Breathe', 'Understand', 'Improve', 'Learn', 'Do'];

export const Home = () => {
  const navigate = useNavigate();
  const settings = getSettings();
  const goals = useMemo(() => getEnabledGoals(), []);
  const dailyPrompt = getDailyPrompt();
  const [aiWelcome, setAiWelcome] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWelcomeMessage = async () => {
      const aiEnabled = isAICompanionEnabled();
      
      if (!aiEnabled || goals.length === 0) {
        setLoading(false);
        return;
      }

      const cached = getAIWelcomeCache();
      if (cached) {
        setAiWelcome(cached);
        setLoading(false);
        return;
      }

      try {
        const result = await generateWelcomeMessage(goals);
        if (result.success) {
          setAiWelcome(result.response);
          saveAIWelcomeCache(result.response);
        }
      } catch (error) {
        console.error('Home AI error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWelcomeMessage();
  }, [goals]);

  return (
    <PageWrapper className="bg-app">
      <div className="max-w-md mx-auto px-6 pt-6 pb-36">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="text-center text-secondary font-body text-sm mb-3 px-4"
        >
          {dailyPrompt}
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-3 flex justify-center"
        >
          <div className="flex flex-col items-start gap-1.5 w-fit mx-auto">
            {acrosticRows.map((row, idx) => (
              <AcrosticRow
                key={row}
                word={row}
                delay={0.22 + idx * 0.05}
              />
            ))}
            <FrameworkBlock
              colorVar={LETTER_COLORS.ME}
              delay={0.62}
              compact
            >
              <span className="acrostic-logo-text" style={{ fontSize: ACROSTIC_FONT_SIZE, fontWeight: 700, fontFamily: 'var(--font-heading)', letterSpacing: 0.3 }}>
                ME
              </span>
            </FrameworkBlock>
          </div>
        </motion.div>

        {goals.length > 0 && (
          <div className="space-y-3 mb-8">
            {goals.map((goal, index) => (
              <motion.div
                key={goal.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="bg-theme-light rounded-3xl p-5 shadow-ambient border border-theme-secondary"
              >
                <p className="text-primary font-body text-sm mb-1 text-center leading-relaxed">
                  {cap(goal.text)} — no matter what
                </p>
                {goal.easyText && (
                  <p className="text-secondary font-body text-sm text-center leading-relaxed">
                    {easyLine(goal.easyText)}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-4"
        >
          {loading ? (
            <div className="flex items-center gap-2 text-secondary">
              <Loader2 size={18} className="animate-spin" />
              <span className="font-body text-sm">Loading...</span>
            </div>
          ) : aiWelcome ? (
            <p className="type-page-title leading-relaxed text-theme-primary">
              {aiWelcome}
            </p>
          ) : (
            <h1 className="type-page-title text-theme-primary">
              Hello {settings?.name || "there"}
            </h1>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate('/daily-log')}
            className="w-full bg-theme-primary text-theme-light rounded-full px-6 py-4 font-body font-medium transition-all hover:opacity-90 hover:-translate-y-0.5 shadow-sm flex items-center justify-center gap-2"
            data-testid="enter-log-btn"
          >
            Enter Log
            <ArrowRight size={18} strokeWidth={2} />
          </button>
        </motion.div>
      </div>
    </PageWrapper>
  );
};

export default Home;